import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { TextFilter } from '@progress/kendo-react-data-tools'
import {
  GridColumn as Column,
  Grid,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import { FlexBox } from 'components/Box'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getWalletFilterFields } from 'components/Filter/walletFields'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridState, useGridStatusState } from 'features/DataGrid'
import uniqBy from 'lodash/uniqBy'
import { useAppDispatch } from 'store'
import {
  fetchFilterAction,
  fetchFilterTypesAction,
  removeFilterAction,
  saveFilterAction,
} from 'store/identity/identity.actions'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery, instanceOfFilterDescriptor } from 'utils/QueryHelpers'

import './Wallets.scss'

import { FilterDescriptor, GridFilterActionState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilterTypes: () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    removeFilter: (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
  }
}

export type SelectWalletEvent = { filterId?: string; value: WalletDto[] }

interface SelectWalletTableProps {
  filterId?: string
  onSelect: (props: SelectWalletEvent) => void
  onClose: () => void
}

const SelectWalletTable: React.FC<SelectWalletTableProps> = (props) => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const { gridState } = useGridState()
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const [wallets, setWallets] = useState<(WalletDto & { selected: boolean })[]>([])
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState()
  )
  const [selectedWallets, setSelectedWallets] = useState<WalletDto[]>([])

  const { fetchFilterTypes, fetchFilters } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterActionState: GridFilterActionState): Promise<PageData<WalletDto[]>> => {
          const response = await walletsApiClient.getWallets({
            pageIndex: gridFilterActionState.pageIndex,
            pageSize: gridFilterActionState.pageSize,
          })

          await fetchFilterTypes()
          await fetchFilters()

          const walletFilter = findFilterById(props.filterId, gridState.filter.filters)
          const value = walletFilter?.value?.filter((wallet: WalletDto) => wallet) || []

          setSelectedWallets(value)
          setWallets(
            response.items.map((wallet: WalletDto) => {
              if (value.length <= 0) {
                return { ...wallet, selected: false }
              }

              const match = value?.find((x: WalletDto) => x.id === wallet.id)
              const isSelected = !!match

              return { ...wallet, selected: isSelected }
            })
          )

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<WalletDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const newGridState = createGridState(getPaginationInitialState(), gridFilterState)
    const response = await callback(newGridState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      setGridFilterState({
        ...newGridState,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
        skip: gridState.skip,
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor): string | null => {
      return buildQuery(filter ?? gridFilterState.filter!) ?? null
    },
    [gridFilterState.filter]
  )

  const filterFetch = useCallback(
    async (params?: {
      query?: string | null
      filter?: CompositeFilterDescriptor
    }): Promise<void> => {
      const searchQuery =
        params?.query !== undefined ? params.query : getQuery(gridFilterState.filter)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await walletsApiClient.getWalletsQuery(searchQuery, pageIndex, pageSize)
      const total = response.totalResults

      setWallets(response.items.map((dataItem: WalletDto) => ({ ...dataItem, selected: false })))
      setGridFilterState({
        ...gridFilterState,
        filter: params?.filter ?? gridFilterState.filter,
        take,
        skip,
        total,
        pageIndex,
      })
    },
    [getQuery, gridFilterState, gridState.pageSize, gridState.take] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const selectWallets = (walletItems: WalletDto[]) =>
    walletItems.map((walletItem: WalletDto) => {
      const match = selectedWallets.find((x) => x.id === walletItem.id)
      const isSelected = !!match
      return { ...walletItem, selected: isSelected }
    })

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip: event.page.skip,
      recordsPerPage: take,
    })

    const searchQuery = getQuery(gridFilterState.filter)
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
    })

    const response = await walletsApiClient.getWallets({
      search: searchQuery,
      pageIndex,
      pageSize,
    })
    const total = response.totalResults

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
      total,
    })
    if (response?.items) {
      setWallets(selectWallets(response.items))
    }
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    setWallets(wallets.map((wallet) => ({ ...wallet, selected: isChecked })))

    const clientsSelected = isChecked
      ? uniqBy([...wallets, ...selectedWallets], 'id')
      : selectedWallets.filter((selectedWallet) => {
          const hasWallet = wallets.find((x) => x.id === selectedWallet.id)

          return !hasWallet
        })

    setSelectedWallets(clientsSelected)
  }

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem.selected) {
      const wallet = wallets.find((x) => x.id === event.dataItem.id)
      if (wallet) {
        setSelectedWallets([...selectedWallets, wallet])
      }
    } else {
      setSelectedWallets(selectedWallets.filter((x) => x.id !== event.dataItem.id))
    }

    setWallets(
      wallets.map((item) => {
        if (item.id === event.dataItem.id) {
          return { ...item, selected: !event.dataItem.selected }
        }
        return item
      })
    )
  }

  const handleSubmitWallets = () => {
    props.onSelect({
      filterId: props.filterId,
      value: uniqBy(selectedWallets, 'id'),
    })
    props.onClose()
  }

  const filterFields = [
    {
      name: 'Client.Name',
      label: 'Client Name',
      filter: TextFilter,
      operators: [
        {
          text: GridFilterOperatorNameEnum.equalOperator,
          operator: GridFilterOperatorEnum.equalOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notEqOperator,
          operator: GridFilterOperatorEnum.notEqOperator,
        },
        {
          text: GridFilterOperatorNameEnum.likeOperator,
          operator: GridFilterOperatorEnum.likeOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notLikeOperator,
          operator: GridFilterOperatorEnum.notLikeOperator,
        },
      ],
    },
    ...getWalletFilterFields(),
  ]

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <div className='wallet-table' style={{ margin: 16 }}>
      <SelectFiltersFacade
        gridState={gridFilterState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={(next) => setGridFilterState((prev) => ({ ...prev, ...next }))}
        filterFetch={filterFetch}
        filterType={FilterType.WALLET}
        filterFields={filterFields}
        containerClassName='wallet-table__filter'
        getQuery={getQuery}
        showFilterWhenInactive
      />
      <Grid
        {...gridFilterState}
        resizable
        data={wallets}
        selectedField='selected'
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        total={gridFilterState.total}
        take={gridFilterState.take}
        skip={gridFilterState.skip}
        onSelectionChange={handleSelectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        onPageChange={handleGridPageChange}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        <Column
          field='selected'
          width='50px'
          headerSelectionValue={wallets.findIndex((dataItem) => !dataItem.selected) === -1}
        />
        <Column field='name' title='Name' />
        <Column field='client.name' title='Type' />
        <Column field='walletType.name' title='Type' />
        <Column field='walletState.name' title='State' />
        <Column field='balance' title='Balance' />
      </Grid>
      <FlexBox style={{ margin: 32 }} alignItems='center' justifyContent='center'>
        <Button style={{ cursor: 'pointer' }} onClick={handleSubmitWallets}>
          Save
        </Button>
      </FlexBox>
    </div>
  )
}

const findFilterById = (
  filterId?: string,
  filters?: (FilterDescriptor | CompositeFilterDescriptor)[]
) => {
  return (
    (filters?.find((filter: FilterDescriptor | CompositeFilterDescriptor) => {
      if (instanceOfFilterDescriptor(filter)) {
        return filter.id === filterId
      }
      return false
    }) as FilterDescriptor) ?? []
  )
}

export default SelectWalletTable
